import { useState, useMemo } from 'react';
import MainCard from 'components/MainCard';
import SkeletonTable from 'components/common/TableSkeleton/SkeletonTable';

import {
  useGetNotifications,
  useReadNotification,
  useDeleteAllNotifications,
  useReadAllNotifications,
  NOTIFICATION_BASE_KEY
} from '../../APIs/Notification/Notification';
import Table from 'react-bootstrap/Table';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import TablePagination from 'components/third-party/react-table/Pagination';
import { mutate } from 'swr';
import Stack from 'react-bootstrap/Stack';
import Badge from 'react-bootstrap/Badge';
import { confirmAction } from '../../components/common/ConfirmationModal';
import { notify } from '@common/NotificationService';

const notificationColumns = (onRead, page, perPage) => [
  {
    header: 'Sl. No',
    cell: ({ row }) => (page - 1) * perPage + (row.index + 1)
  },

  {
    header: 'Title',
    accessorKey: 'title',
    cell: ({ row }) => (
      <Stack>
        <h6 className="mb-1">{row.original.title}</h6>
      </Stack>
    )
  },
  {
    header: 'Description',
    accessorKey: 'message',
    cell: ({ row }) => (
      <Stack>
        <span className="text-truncate d-inline-block" style={{ maxWidth: '620px' }} title={row.original.message}>
          {row.original.message}
        </span>
      </Stack>
    )
  },
  {
    header: 'Status',
    accessorKey: 'is_read',
    cell: ({ getValue }) => <Badge bg={getValue() ? 'secondary' : 'success'}>{getValue() ? 'Read' : 'Unread'}</Badge>
  },
  {
    header: 'Received At',
    accessorKey: 'created_at'
  }
];

function NotificationTable({ data, columns, pagination, page, perPage, setPage, setPerPage }) {
  const table = useReactTable({
    data,
    columns,
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize: perPage
      }
    },
    manualPagination: true,
    pageCount: pagination?.last_page ?? 1,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <>
      <Table hover responsive className="mb-0 border-top">
        <colgroup>
          {/* slnpo  */}
          <col style={{ width: '10%' }} />
          {/* Title  */}
          <col style={{ width: '20%' }} />
          {/* Description */}
          <col style={{ width: '30%' }} />
          {/* Status */}
          <col style={{ width: '10%' }} />
          {/* Received At */}
          <col style={{ width: '15%' }} />
        </colgroup>

        <thead>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {group.headers.map((header) => (
                <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr className="gc-empty-row">
              <td colSpan={table.getVisibleLeafColumns().length} className="text-center text-muted py-5">
                <i className="ph ph-bell d-block mb-2" style={{ fontSize: 40, opacity: 0.35 }} />
                No notifications to show
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <TablePagination
        getPageCount={() => pagination?.last_page ?? 1}
        setPageIndex={(index) => setPage(index + 1)}
        getState={() => ({
          pagination: {
            pageIndex: page - 1,
            pageSize: perPage
          }
        })}
        totalEntries={pagination?.total}
        initialPageSize={perPage}
      />
    </>
  );
}

export default function ViewAllNotifications() {
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);

  const { notifications, unreadCount, pagination, isLoading, refresh } = useGetNotifications({ page, per_page: perPage });
  const { deleteAll } = useDeleteAllNotifications();
  const { readOne } = useReadNotification();
  const { readAll } = useReadAllNotifications();

  const handleRead = async (id) => {
    await readOne(id);
    refresh();
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await readAll();
      if (res?.success) {
        notify.success('All notifications marked as read', '');
        mutate((key) => typeof key === 'string' && key.startsWith(NOTIFICATION_BASE_KEY), undefined, { revalidate: true });
      }
    } catch (err) {
      notify.error('Failed to mark all as read', '');
    }
  };

  const handleDeleteAll = async () => {
    const confirmed = await confirmAction({
      title: 'Delete all notifications?',
      message: 'This action cannot be undone.',
      confirmText: 'Delete'
    });
    if (!confirmed) return;
    try {
      const res = await deleteAll();
      if (res?.success) {
        notify.success(res.message || 'All notifications deleted', 'delete');

        mutate((key) => typeof key === 'string' && key.startsWith(NOTIFICATION_BASE_KEY), undefined, { revalidate: true });
      }
    } catch (err) {
      notify.error(err?.response?.data?.message || 'Failed to delete notifications', 'delete');
    }
  };

  const columns = useMemo(() => notificationColumns(handleRead, page, perPage), [page]);

  return (
    <MainCard
      title="All Notifications"
      secondary={
        <Stack direction="horizontal" gap={2}>
          {/* Nothing unread anywhere in the list — the action would do nothing,
              so it is not offered. Delete all still has rows to act on. */}
          {unreadCount > 0 && (
            <button className="btn btn-outline-primary btn-sm" onClick={handleMarkAllRead}>
              Mark all read
            </button>
          )}

          <button className="btn btn-outline-danger btn-sm" onClick={handleDeleteAll} disabled={!notifications.length}>
            Delete all
          </button>
        </Stack>
      }
      className="table-card app-tinted-card"
    >
      {isLoading ? (
        <SkeletonTable rows={6} columns={5} />
      ) : (
        <NotificationTable data={notifications} columns={columns} pagination={pagination} page={page} perPage={perPage} setPage={setPage} />
      )}
    </MainCard>
  );
}
